import { useQuery } from '@tanstack/react-query'
import {
  reportsApi,
  type BacklogReport,
  type ConsumptionReport,
  type ExpiryReport,
  type StockReport,
  type OnTimeReport,
  type TurnaroundReport,
  type ProcedureVolumeReport,
  type ThroughputReport,
} from '@/api/reportsApi'
import { useTenant } from '@/providers/TenantProvider'
import { toQueryWindow, type ReportTimeFrame } from '@/lib/reportRange'
import type { ReportFilterSelection } from '@/lib/reportFilters'

/**
 * Report 1.1's data.
 *
 * `selectedTenantId` is in the query key, not merely relied on through
 * `Layout`'s remount: a report is an aggregate over one tenant's runs, and a
 * cached bucket series from the tenant just left must never paint here.
 *
 * The window the reader sees is inclusive at both ends; `toQueryWindow` is the
 * single place it becomes the half-open range the endpoint takes. Doing it here
 * rather than in the component keeps the last day of every report from
 * quietly disappearing.
 */
export function useProcedureVolumeReport(frame: ReportTimeFrame, filters: ReportFilterSelection) {
  const { selectedTenantId: tenantId } = useTenant()
  const window = toQueryWindow(frame.window)
  const types = filters.type ?? []

  return useQuery<ProcedureVolumeReport>({
    queryKey: [
      'reports',
      'procedures',
      'volume',
      tenantId,
      window.from,
      window.to,
      frame.grain,
      // Sorted, so ticking two types in either order is one cache entry.
      [...types].sort().join(','),
    ],
    enabled: !!tenantId,
    queryFn: () =>
      reportsApi.procedureVolume({
        from: window.from,
        to: window.to,
        grain: frame.grain,
        types,
      }),
  })
}

/**
 * Shared shape of every report query here: the tenant, the half-open window,
 * the grain where one applies, and the sorted type filter.
 *
 * Sorted so ticking two types in either order is one cache entry rather than
 * two identical requests.
 */
function useReportKey(frame: ReportTimeFrame, filters: ReportFilterSelection) {
  const { selectedTenantId: tenantId } = useTenant()
  const window = toQueryWindow(frame.window)
  const types = [...(filters.type ?? [])].sort()
  return { tenantId, window, types, grain: frame.grain }
}

/** Report 1.2 — procedures received vs completed. */
export function useProcedureThroughputReport(
  frame: ReportTimeFrame,
  filters: ReportFilterSelection,
) {
  const { tenantId, window, types, grain } = useReportKey(frame, filters)
  return useQuery<ThroughputReport>({
    queryKey: ['reports', 'procedures', 'throughput', tenantId, window.from, window.to, grain, types.join(',')],
    enabled: !!tenantId,
    queryFn: () => reportsApi.procedureThroughput({ from: window.from, to: window.to, grain, types }),
  })
}

/**
 * Report 1.3 — on-time completion.
 *
 * The grain is deliberately NOT in the key: this report is one figure over the
 * window, so changing the bucket size must not refetch it.
 */
export function useProcedureOnTimeReport(frame: ReportTimeFrame, filters: ReportFilterSelection) {
  const { tenantId, window, types } = useReportKey(frame, filters)
  return useQuery<OnTimeReport>({
    queryKey: ['reports', 'procedures', 'on-time', tenantId, window.from, window.to, types.join(',')],
    enabled: !!tenantId,
    queryFn: () => reportsApi.procedureOnTime({ from: window.from, to: window.to, types }),
  })
}

/**
 * Report 1.4's data.
 *
 * Same key shape as the others. No grain — the window is summarised, not
 * bucketed — so a grain in the key would split the cache on a control the
 * report does not have.
 */
export function useProcedureTurnaroundReport(
  frame: ReportTimeFrame,
  filters: ReportFilterSelection,
) {
  const { tenantId, window, types } = useReportKey(frame, filters)
  return useQuery<TurnaroundReport>({
    queryKey: ['reports', 'procedures', 'turnaround', tenantId, window.from, window.to, types.join(',')],
    enabled: !!tenantId,
    queryFn: () => reportsApi.procedureTurnaround({ from: window.from, to: window.to, types }),
  })
}

/**
 * Report 1.5's data — a snapshot as of the window's end.
 *
 * `window.from` stays in the key even though the endpoint ignores it: the key
 * describes what the reader asked for, and dropping it would serve a cached
 * snapshot under a period the reader has since changed.
 */
export function useProcedureBacklogReport(
  frame: ReportTimeFrame,
  filters: ReportFilterSelection,
) {
  const { tenantId, window, types } = useReportKey(frame, filters)
  return useQuery<BacklogReport>({
    queryKey: ['reports', 'procedures', 'backlog', tenantId, window.from, window.to, types.join(',')],
    enabled: !!tenantId,
    queryFn: () => reportsApi.procedureBacklog({ from: window.from, to: window.to, types }),
  })
}

/**
 * Report 2.1's data.
 *
 * The window is deliberately NOT in the key: the answer does not depend on it,
 * so keying by it would cache three identical copies and refetch on every
 * period change for no new information.
 */
export function useInventoryStockReport(filters: ReportFilterSelection) {
  const { selectedTenantId: tenantId } = useTenant()
  const categories = [...(filters.category ?? [])].sort()
  return useQuery<StockReport>({
    queryKey: ['reports', 'inventory', 'stock', tenantId, categories.join(',')],
    enabled: !!tenantId,
    queryFn: () => reportsApi.inventoryStock({ categories }),
  })
}

/** Report 2.2's data — window-bounded, unlike 2.1. */
export function useInventoryConsumptionReport(
  frame: ReportTimeFrame,
  filters: ReportFilterSelection,
) {
  const { tenantId, window, types } = useReportKey(frame, filters)
  const categories = [...(filters.category ?? [])].sort()
  return useQuery<ConsumptionReport>({
    queryKey: [
      'reports',
      'inventory',
      'consumption',
      tenantId,
      window.from,
      window.to,
      types.join(','),
      categories.join(','),
    ],
    enabled: !!tenantId,
    queryFn: () =>
      reportsApi.inventoryConsumption({ from: window.from, to: window.to, types, categories }),
  })
}

/**
 * Report 2.3's data.
 *
 * The window's LENGTH is the horizon, so both ends stay in the key: a reader
 * who moves a custom window without changing its length is asking the same
 * question, but proving that here would mean re-deriving the length in two
 * places.
 */
export function useInventoryExpiryReport(
  frame: ReportTimeFrame,
  filters: ReportFilterSelection,
) {
  const { tenantId, window } = useReportKey(frame, filters)
  const categories = [...(filters.category ?? [])].sort()
  return useQuery<ExpiryReport>({
    queryKey: ['reports', 'inventory', 'expiry', tenantId, window.from, window.to, categories.join(',')],
    enabled: !!tenantId,
    queryFn: () => reportsApi.inventoryExpiry({ from: window.from, to: window.to, categories }),
  })
}

/**
 * The material categories the category filter offers.
 *
 * Tenant master data, not report output, so it is fetched once and shared by
 * every report that filters on it — and it is available before any report has
 * loaded, which is the point.
 */
export function useMaterialCategories() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery<{ categories: string[] }>({
    queryKey: ['reports', 'inventory', 'categories', tenantId],
    enabled: !!tenantId,
    queryFn: () => reportsApi.materialCategories(),
  })
}
